import { BASIS_POINTS, FEE_BPS, GRADUATION_THRESHOLD, PHANTOM_QUOTE, SUPPLY } from './constants.js';
/**
 * Off-chain mirror of `CurveMath` and `Curve`'s quote functions, used for
 * instant previews as the user types. Every path here is the integer arithmetic
 * the contract performs, not a floating-point approximation, so a preview and
 * the chain agree to the wei. The contract's own `quoteBuy` / `quoteSell` remain
 * the authority and the site checks against them before it submits.
 */
/** `CurveMath.quoteAmountOut`: the lenient form, zero where the strict one reverts. */
export function quoteAmountOut(amountIn, reserveIn, reserveOut, feeBps = 0n) {
    if (amountIn <= 0n || reserveIn <= 0n || reserveOut <= 0n || feeBps >= BASIS_POINTS)
        return 0n;
    const amountInWithFee = amountIn * (BASIS_POINTS - feeBps);
    return (amountInWithFee * reserveOut) / (reserveIn * BASIS_POINTS + amountInWithFee);
}
/** `CurveMath.getAmountIn`, including its `+ 1` rounding in the contract's favour. */
export function getAmountIn(amountOut, reserveIn, reserveOut, feeBps = 0n) {
    if (amountOut <= 0n || reserveIn <= 0n || reserveOut <= amountOut || feeBps >= BASIS_POINTS)
        return 0n;
    return (amountOut * reserveIn * BASIS_POINTS) / ((reserveOut - amountOut) * (BASIS_POINTS - feeBps)) + 1n;
}
export function reserves(s) {
    return { quote: PHANTOM_QUOTE + s.trackedQuote, tokens: s.trackedTokens };
}
/** `Curve.sellableTokens()`. */
export function sellableTokens(s) {
    return s.trackedTokens > s.reservedTokens ? s.trackedTokens - s.reservedTokens : 0n;
}
/** `Curve.readyToGraduate()`. */
export function readyToGraduate(s) {
    return !s.graduated && sellableTokens(s) === 0n;
}
/**
 * `Curve.quoteBuy` / `quoteBuyFor` plus the clamp-and-refund branch of `Curve.buy`,
 * so the preview shows what a graduating buy really costs rather than the amount
 * the user typed.
 */
export function quoteBuy(s, quoteIn, taxBps = 0n) {
    const empty = { tokensOut: 0n, spent: 0n, refund: quoteIn, fee: 0n, tax: 0n, graduates: false };
    if (s.graduated || quoteIn <= 0n)
        return empty;
    const sellable = sellableTokens(s);
    if (sellable === 0n)
        return empty;
    const { quote: q, tokens: t } = reserves(s);
    let spent = quoteIn;
    let fee = (spent * FEE_BPS) / BASIS_POINTS;
    let tax = ((spent - fee) * taxBps) / BASIS_POINTS;
    let tokensOut = quoteAmountOut(spent - fee - tax, q, t);
    if (tokensOut > sellable) {
        tokensOut = sellable;
        const net = getAmountIn(sellable, q, t);
        // Gross the clamped fill back up so the fee and any tax still come out of the
        // input, rounding up as `Math.mulDiv(..., Ceil)` does on chain.
        const num = net * BASIS_POINTS * BASIS_POINTS;
        const den = (BASIS_POINTS - FEE_BPS) * (BASIS_POINTS - taxBps);
        const grossed = num / den + (num % den === 0n ? 0n : 1n);
        spent = grossed < quoteIn ? grossed : quoteIn;
        fee = (spent * FEE_BPS) / BASIS_POINTS;
        tax = ((spent - fee) * taxBps) / BASIS_POINTS;
    }
    return { tokensOut, spent, refund: quoteIn - spent, fee, tax, graduates: tokensOut === sellable };
}
/** `Curve.quoteSell`: gross out from the token side, then the fee off the output. */
export function quoteSell(s, tokensIn) {
    if (s.graduated || readyToGraduate(s) || tokensIn <= 0n)
        return { quoteOut: 0n, fee: 0n };
    const { quote: q, tokens: t } = reserves(s);
    const gross = quoteAmountOut(tokensIn, t, q);
    if (gross > s.trackedQuote)
        return { quoteOut: 0n, fee: 0n };
    const fee = (gross * FEE_BPS) / BASIS_POINTS;
    return { quoteOut: gross - fee, fee };
}
/** Spot price in wei per whole token, from the curve's reserves. */
export function spotPriceWei(s) {
    const { quote, tokens } = reserves(s);
    if (tokens === 0n)
        return 0n;
    return (quote * 10n ** 18n) / tokens;
}
/** How far the curve is from graduating, in parts per million of the raise target. */
export function progressPpm(s) {
    if (s.graduated)
        return 1_000_000;
    const p = (s.trackedQuote * 1000000n) / GRADUATION_THRESHOLD;
    return Number(p > 1000000n ? 1000000n : p);
}
/** The reserved allocation a fresh curve fixes at `initialize`. */
export function initialReservedTokens() {
    return (SUPPLY * PHANTOM_QUOTE) / (PHANTOM_QUOTE + GRADUATION_THRESHOLD);
}
//# sourceMappingURL=curve.js.map